import { useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Button, Stack } from '@lyearn/ui';
import { EditorMode } from '@/components/ContentEditor/types';
import Form from '@/components/Form';
import { Modal } from '@/components/Modals';
import { ModalVariant } from '@/components/Modals/types';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { EventSelectionInput } from '@/types/schema';

import ConfirmAssignmentDialog from '../../../AssignmentCreator/components/ConfirmAssignmentDialog';
import {
  AssignmentFormValues,
  getFormFields,
  getInitialValues,
  getSectionLayout,
  validateForm,
} from '../../../AssignmentCreator/components/Editor/config';
import useAssignmentCreator from '../../../AssignmentCreator/hooks/useAssignmentCreator';
import useAssignmentHeader from '../../../AssignmentCreator/hooks/useAssignmentHeader';
import { AssignmentCreatorInitialContentType } from '../../../AssignmentCreator/type';
import { getScheduledDate } from '../../../AssignmentCreator/utils';

const Footer = ({ onAssign, loading }: { onAssign: () => void; loading: boolean }) => {
  const { t } = useTranslation(['AssignmentCreator']);
  return (
    <Stack className="border-t border-divider py-10 flex justify-end px-24">
      <Button loading={loading} onClick={onAssign}>
        {t('AssignmentCreator:Assign')}
      </Button>
    </Stack>
  );
};

function AssignContentModal({
  onClose,
  initialValues,
}: {
  onClose: () => void;
  initialValues?: Partial<AssignmentCreatorInitialContentType>;
}) {
  const { t } = useTranslation(['AssignmentCreator']);
  const { site } = useAppConfig();
  const currentUser = useUserContext();
  const {
    onSubmit,
    setSubmitRef,
    onAssign,
    localFormData,
    onConfirmAssignmentSubmit,
    onDialogClose,
    fetching,
  } = useAssignmentCreator({
    mode: EditorMode.Create,
    isInModal: true,
  });
  const [fetchingStats, setFetchingStats] = useState<boolean | undefined>();

  const initialFormValues = useMemo(() => {
    return getInitialValues(currentUser.id, undefined, undefined, initialValues);
  }, [currentUser.id, initialValues]);

  const { formFields, sectionLayout, validate } = useMemo(() => {
    return {
      formFields: getFormFields(t, []),
      sectionLayout: getSectionLayout(t, site.canConfigureAssignmentNotification),
      validate: validateForm(t),
    };
  }, [site.canConfigureAssignmentNotification, t]);

  const { methods } = useAssignmentHeader({
    assignmentNumber: undefined,
    onAssign,
  });
  const { onDirectAssign } = methods;

  return (
    <Stack>
      <Modal
        footer={<Footer loading={Boolean(fetchingStats)} onAssign={onDirectAssign} />}
        header={{
          title: t('AssignmentCreator:Assignment'),
          description: t('AssignmentCreator:NewAssignmentDescription'),
        }}
        variant={ModalVariant.FullScreen}
        onClose={onClose}>
        <Stack className="flex-1 px-24 py-30 mx-auto" direction="col">
          <Form<AssignmentFormValues>
            fields={formFields}
            formName="Assignment Creator"
            initialValues={initialFormValues}
            sectionLayout={sectionLayout}
            setSubmitRef={setSubmitRef}
            validate={validate}
            onSubmit={onSubmit}
          />
        </Stack>
      </Modal>

      {localFormData ? (
        <ConfirmAssignmentDialog
          collectionIds={localFormData.collectionIds}
          contentIds={localFormData.contentIds}
          eventSelections={localFormData.eventSelections as EventSelectionInput[] | undefined}
          fetching={fetching}
          importedUserGroupIds={localFormData?.importedUserGroupIds ?? []}
          learnerIds={localFormData.userIds}
          removedUserIds={localFormData?.removedUserIds ?? []}
          scheduledDate={getScheduledDate(localFormData.scheduleDateConfig)}
          setFetchingStats={setFetchingStats}
          userGroupIds={localFormData.userGroupIds}
          onClose={onDialogClose}
          onSubmit={onConfirmAssignmentSubmit}
        />
      ) : null}
    </Stack>
  );
}

export default AssignContentModal;
